import React, { useRef, useState } from "react";
import type { MentionPopoverRef } from "./MentionPopover";
import { MentionCallbacks, MentionState } from "./extensions/MentionExtension";
import { OnYoutubePaste } from "./extensions/YoutubeExtension";
import { OpenLinkPopover } from "./extensions/LinkKeyboardShortcutExtension";
import { useRichTextEditor } from "./useRichTextEditor";
import { DEFAULT_ALLOWED_MIME_TYPES } from "./extensions/ImageUploadExtension";
import type { RichTextEditorProps } from "../types";
import {
  RichTextEditorContext,
  RichTextEditorContextValue,
} from "./RichTextEditorContext";

type RichTextEditorProviderProps = Pick<
  RichTextEditorProps,
  | "value"
  | "defaultValue"
  | "onChange"
  | "onJsonChange"
  | "contentType"
  | "onMarkdownChange"
  | "extensions"
  | "placeholder"
  | "disabled"
  | "allowTextFormat"
  | "allowTextAlignment"
  | "allowLists"
  | "allowChecklist"
  | "allowCode"
  | "allowBlockquote"
  | "allowLink"
  | "allowMedia"
  | "allowMention"
  | "allowDragDrop"
  | "mentionOptions"
  | "onMentionInputChange"
  | "renderMentionHoverContent"
  | "onImageUpload"
  | "isAllowedImageSrc"
> & {
  allowedMimeTypes?: `image/${string}`[];
  children: React.ReactNode;
};

export const RichTextEditorProvider = ({
  children,
  allowTextFormat = true,
  allowTextAlignment = true,
  allowLists = false,
  allowChecklist = false,
  allowCode = false,
  allowBlockquote = false,
  allowLink = false,
  allowMedia = false,
  allowMention = false,
  allowDragDrop = false,
  disabled = false,
  mentionOptions,
  onMentionInputChange,
  renderMentionHoverContent,
  onImageUpload,
  allowedMimeTypes,
  ...editorProps
}: RichTextEditorProviderProps) => {
  // --- Mention state & refs ---
  const mentionOptionsRef = useRef(mentionOptions ?? []);
  mentionOptionsRef.current = mentionOptions ?? [];

  const onMentionInputChangeRef = useRef(onMentionInputChange);
  onMentionInputChangeRef.current = onMentionInputChange;

  const renderMentionHoverContentRef = useRef(renderMentionHoverContent);
  renderMentionHoverContentRef.current = renderMentionHoverContent;

  const [mentionState, setMentionState] = useState<MentionState>({
    open: false,
    items: [],
    command: null,
    rect: null,
  });
  const mentionPopoverRef = useRef<MentionPopoverRef>(null);
  const mentionCallbacksRef = useRef<MentionCallbacks>({
    getOptions: () => mentionOptionsRef.current,
    onQueryChange: (query) => onMentionInputChangeRef.current?.(query),
    setState: setMentionState,
    onKeyDown: (event) =>
      mentionPopoverRef.current?.onKeyDown({ event }) ?? false,
    getRenderHoverContent: () => renderMentionHoverContentRef.current,
  });

  // --- Image upload ref ---
  const onImageUploadRef = useRef(onImageUpload);
  onImageUploadRef.current = onImageUpload;

  // --- Link keyboard shortcut ref ---
  const openLinkPopoverRef = useRef<OpenLinkPopover | null>(null);

  // --- Link popover state ---
  const [linkPopoverState, setLinkPopoverState] = useState<
    RichTextEditorContextValue["linkPopoverState"]
  >({
    open: false,
    anchorRect: null,
    initialHref: "",
    initialDisplayText: "",
  });

  // --- YouTube popover state ---
  const [youtubePopoverState, setYoutubePopoverState] = useState<
    RichTextEditorContextValue["youtubePopoverState"]
  >({ open: false, anchorRect: null });

  // --- YouTube embed prompt state ---
  const [youtubeEmbedPromptState, setYoutubeEmbedPromptState] = useState<
    RichTextEditorContextValue["youtubeEmbedPromptState"]
  >({
    open: false,
    anchorRect: null,
    url: "",
    from: 0,
    to: 0,
  });
  const onYoutubePasteRef = useRef<OnYoutubePaste>((params) => {
    setYoutubeEmbedPromptState({ open: true, ...params });
  });

  // --- Editor ---
  const editor = useRichTextEditor({
    ...editorProps,
    disabled,
    allowTextFormat,
    allowTextAlignment,
    allowLists,
    allowChecklist,
    allowCode,
    allowBlockquote,
    allowLink,
    allowMedia,
    allowMention,
    allowedMimeTypes,
    onImageUploadRef,
    mentionCallbacksRef,
    onYoutubePasteRef,
    openLinkPopoverRef,
  });

  // --- Handlers ---
  const openLinkPopover = (params: {
    anchorRect: DOMRect;
    href: string;
    displayText: string;
  }) => {
    setLinkPopoverState({
      open: true,
      anchorRect: params.anchorRect,
      initialHref: params.href,
      initialDisplayText: params.displayText,
    });
  };
  openLinkPopoverRef.current = openLinkPopover;

  const closeLinkPopover = () =>
    setLinkPopoverState((prev) => ({ ...prev, open: false }));

  const removeLinkPopover = () => {
    editor?.chain().focus().unsetLink().run();
    closeLinkPopover();
  };

  const normalizeHref = (href: string) => {
    // Preserve URLs that already use a supported scheme.
    if (/^(https?|mailto|tel):/i.test(href)) {
      return href;
    }

    // Preserve relative paths and fragment identifiers as-is.
    if (
      href.startsWith("/") ||
      href.startsWith("#") ||
      href.startsWith("./") ||
      href.startsWith("../")
    ) {
      return href;
    }

    // Fallback: treat remaining values as hostnames and prefix with https.
    return `https://${href}`;
  };

  const saveLinkPopover = (href: string, displayText: string) => {
    if (!editor) return;
    const trimmedHref = href.trim();
    if (!trimmedHref) {
      editor.chain().focus().unsetLink().run();
      closeLinkPopover();
      return;
    }
    const normalizedHref = normalizeHref(trimmedHref);
    const { from, to, empty } = editor.state.selection;
    if (empty) {
      editor
        .chain()
        .focus()
        .insertContent({
          type: "text",
          text: displayText || normalizedHref,
          marks: [{ type: "link", attrs: { href: normalizedHref } }],
        })
        .run();
    } else if (
      displayText &&
      displayText !== editor.state.doc.textBetween(from, to)
    ) {
      editor
        .chain()
        .focus()
        .deleteSelection()
        .insertContent({
          type: "text",
          text: displayText,
          marks: [{ type: "link", attrs: { href: normalizedHref } }],
        })
        .run();
    } else {
      editor.chain().focus().setLink({ href: normalizedHref }).run();
    }
    closeLinkPopover();
  };

  const closeYoutubeEmbedPrompt = () =>
    setYoutubeEmbedPromptState((prev) => ({ ...prev, open: false }));

  const embedYoutubeFromPrompt = () => {
    if (editor && youtubeEmbedPromptState.url) {
      const { from, to, url } = youtubeEmbedPromptState;
      editor
        .chain()
        .focus()
        .deleteRange({ from, to })
        .setYoutubeVideo({ src: url })
        .run();
    }
    closeYoutubeEmbedPrompt();
  };

  const openYoutubePopover = (anchorRect: DOMRect) =>
    setYoutubePopoverState({ open: true, anchorRect });

  const closeYoutubePopover = () =>
    setYoutubePopoverState((prev) => ({ ...prev, open: false }));

  const saveYoutubePopover = (url: string) => {
    if (editor && url) {
      editor.chain().focus().setYoutubeVideo({ src: url }).run();
    }
    closeYoutubePopover();
  };

  const onEditorClick = (event: React.MouseEvent) => {
    if (!allowLink || !editor) return;
    const anchor = (event.target as HTMLElement).closest("a[href]");
    if (!anchor) return;
    const href = anchor.getAttribute("href") ?? "";
    if (event.metaKey || event.ctrlKey) {
      if (href) window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    const rect = anchor.getBoundingClientRect();
    openLinkPopover({
      href,
      displayText: anchor.textContent ?? "",
      anchorRect: rect,
    });
  };

  return (
    <RichTextEditorContext.Provider
      value={{
        editor,
        disabled,
        allowTextFormat,
        allowTextAlignment,
        allowLists,
        allowChecklist,
        allowCode,
        allowBlockquote,
        allowLink,
        allowMedia,
        allowMention,
        allowDragDrop,
        onImageUpload,
        allowedMimeTypes: allowedMimeTypes ?? DEFAULT_ALLOWED_MIME_TYPES,
        mentionState,
        mentionPopoverRef,
        linkPopoverState,
        youtubePopoverState,
        youtubeEmbedPromptState,
        onEditorClick,
        openLinkPopover,
        closeLinkPopover,
        removeLinkPopover,
        saveLinkPopover,
        embedYoutubeFromPrompt,
        closeYoutubeEmbedPrompt,
        openYoutubePopover,
        closeYoutubePopover,
        saveYoutubePopover,
      }}
    >
      {children}
    </RichTextEditorContext.Provider>
  );
};
